import React, { useState } from 'react';

import { Formik } from 'formik';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';

import { hideGlobalDialog } from '../../../../state/ui/dialog';
import { updateMonitoredObject } from '../../../../state/app/registers/monitoredObjects';

import DialogTitle from '../../../dialogs/DialogTitle';

import { useAppDispatch } from '../../../../hooks';
import useToggle from '../../../../hooks/useToggle';
import TranslationHelper from '../../../../helpers/TranslationHelper';
import useAsyncThunkWithSnackbar from '../../../../hooks/useAsyncThunkWithSnackbar';

import { IEditObjectDialogFormValues, IHeaderData } from './types';
import { prepareMonitoredObject } from './utils/utils';
import EditObjectForm from './components/EditObjectForm';
import { useStyles } from './Themable.hooks';
import { TAttributeDictionary } from '../../../../features/Attributes/types';
import { validateAllErrors } from '../../../../features/Attributes/utils/validation';
import { schemas } from './utils/fields';

interface IOwnProps {
    headerData: IHeaderData;
    onSuccess: () => void;
}

const EditObjectDialog = ({ headerData, onSuccess }: IOwnProps) => {
    const handleAsyncRequest = useAsyncThunkWithSnackbar();
    const dispatch = useAppDispatch();

    const { isOpen, handleToggle } = useToggle(true);
    const [loading, setLoading] = useState(false);

    const classes = useStyles();
    const initialValues: IEditObjectDialogFormValues = {
        data: {
            name: '',
            timeZone: undefined,
            group: undefined,
            externalId: undefined,
            attributes: [],
            id: undefined,
            groups: [],
            type: undefined,
            registry: undefined,
        },
        attributes: {} as TAttributeDictionary,
    };

    const handleClose = (event?: object, reason?: string) => {
        if (reason === 'backdropClick') {
            return;
        }
        dispatch(hideGlobalDialog());
        handleToggle();
    };

    const submitForm = async (
        values: IEditObjectDialogFormValues,
        resetForm: () => void,
        handleClose: () => void,
        onSuccess: () => void
    ) => {
        const monitoredObject = prepareMonitoredObject(values);
        if (!monitoredObject) return;

        await handleAsyncRequest({
            asyncAction: updateMonitoredObject({ monitoredObject }),
            onSuccess: {
                callback: () => {
                    resetForm();
                    handleClose();
                    onSuccess();
                },
                message: TranslationHelper.translate(
                    'Update saved successfully'
                ),
            },
        });
    };

    const handleSubmit = async (
        values: IEditObjectDialogFormValues,
        { resetForm, setErrors }: any
    ) => {
        if (!values.data?.type) return;
        const schema = schemas[values.data.type]();

        const allErrors = await validateAllErrors(
            schema,
            values.attributes,
            values
        );

        if (allErrors) {
            setErrors(allErrors);
            return;
        }

        await submitForm(values, resetForm, handleClose, onSuccess);
    };

    return (
        <Formik
            enableReinitialize={true}
            initialValues={initialValues}
            onSubmit={(values, formikHelpers) =>
                handleSubmit(values, formikHelpers)
            }
            validateOnBlur={false}
            validateOnChange={false}
            validationSchema={{}}
        >
            {({ errors, values, setFieldValue, submitForm }) => (
                <Dialog
                    open={isOpen}
                    onClose={handleClose}
                    maxWidth={'md'}
                    disableEscapeKeyDown
                >
                    <DialogTitle
                        title={TranslationHelper.translate('Edit object')}
                        save={submitForm}
                        close={handleClose}
                        loading={loading}
                    />
                    <DialogContent className={classes.dialogContent}>
                        <EditObjectForm
                            headerData={headerData}
                            formValues={values}
                            errors={errors}
                            setFieldValue={setFieldValue}
                            loading={loading}
                            setLoading={setLoading}
                        />
                    </DialogContent>
                </Dialog>
            )}
        </Formik>
    );
};

export default EditObjectDialog;
